import React from "react";

/**
 * CarrierStrip — infinite marquee of underwriter logos, each presented
 * inside the bespoke ornate ship-chest frame (chest-frame.png). The chest
 * frame PNG has a real alpha cutout (~19% inset on the sides, ~32% top,
 * ~20% bottom), so each carrier logo is positioned in the inner rectangle
 * and the chest frame overlays it. A SVG ocean (rolling waves) sits as
 * the animated backdrop behind the entire strip to tie into the Endurance
 * maritime brand.
 */

const CARRIER_ASSET_BASE = "/carriers";
// Cache-buster suffix — bump this string whenever the chest PNG is updated
// so the browser refetches it instead of serving a stale copy.
const CHEST_FRAME = "/carriers/chest-frame.png?v=3";

// Inner cutout — measured from chest-frame.png (1536×1024)
const CUTOUT = {
    top: "34.3%",
    left: "21.6%",
    right: "21.5%",
    bottom: "18.8%",
};

const CARRIERS = [
    { name: "AFLAC", slug: "aflac" },
    { name: "AIG", slug: "aig" },
    { name: "American Family Insurance", slug: "american_family_insurance" },
    { name: "Arch Insurance", slug: "arch_insurance" },
    { name: "Attune Insurance", slug: "attune_insurance" },
    { name: "AU Gold (AmTrust Online)", slug: "au_gold_amtrust" },
    { name: "Berkley Insurance", slug: "berkley_insurance" },
    {
        name: "Berkshire Hathaway Insurance Group",
        slug: "berkshire_hathaway_insurance",
    },
    { name: "biBERK", slug: "biberk" },
    { name: "Blitz Insurance Platform", slug: "blitz_insurance" },
    { name: "Chubb", slug: "chubb" },
    { name: "CNA", slug: "cna" },
    { name: "Coterie Insurance", slug: "coterie_insurance" },
    { name: "Cowbell Cyber", slug: "cowbell_cyber" },
    { name: "Draftrs", slug: "draftrs", wordmark: true },
    { name: "DUAL North America", slug: "dual_north_america" },
    { name: "ERGO Next Insurance Services", slug: "ergo_next" },
    { name: "Farmers Insurance", slug: "farmers_insurance" },
    { name: "First Insurance Funding", slug: "first_insurance_funding" },
    { name: "Foxquilt Insurance", slug: "foxquilt_insurance" },
    {
        name: "Great American Insurance Group",
        slug: "great_american_insurance",
    },
    { name: "Hiscox USA", slug: "hiscox_usa" },
    { name: "iBynd Insurance Platform", slug: "ibynd_insurance" },
    { name: "Liberty Mutual", slug: "liberty_mutual" },
    { name: "LIO Insurance", slug: "lio_insurance" },
    { name: "MassMutual", slug: "massmutual" },
    { name: "MetLife", slug: "metlife" },
    { name: "MGT Insurance", slug: "mgt_insurance" },
    { name: "Mutual of Omaha", slug: "mutual_of_omaha" },
    { name: "National Life Group", slug: "national_life_group" },
    { name: "Nationwide", slug: "nationwide" },
    { name: "Neptune Flood", slug: "neptune_flood" },
    { name: "NormanMax Insurance Holdings", slug: "normanmax_insurance" },
    { name: "Physicians Mutual", slug: "physicians_mutual" },
    { name: "Pie Insurance", slug: "pie_insurance" },
    { name: "Progressive", slug: "progressive" },
    { name: "Prudential Financial", slug: "prudential_financial" },
    { name: "RLI Surety", slug: "rli_surety" },
    { name: "Simply Business", slug: "simply_business" },
    { name: "Slice Insurance Technologies", slug: "slice_insurance" },
    { name: "The Hartford", slug: "the_hartford" },
    { name: "THREE by Berkshire Hathaway", slug: "three_berkshire" },
    { name: "Travelers", slug: "travelers" },
    { name: "UnitedHealthcare", slug: "unitedhealthcare" },
    { name: "USAA", slug: "usaa" },
    { name: "USLI", slug: "usli" },
    { name: "Zurich North America", slug: "zurich_north_america" },
];

function CarrierChest({ name, slug, wordmark }) {
    return (
        <div
            data-testid="carrier-card"
            className="group/chest relative flex w-[120px] shrink-0 flex-col items-center sm:w-[136px]"
        >
            {/* Chest aspect ratio matches the source PNG (1536:1024 = 3:2) */}
            <div className="relative w-full" style={{ aspectRatio: "1536 / 1024" }}>
                {/* Layer 1 — carrier logo positioned inside the cutout
                    (a slight cream parchment behind so it reads on the
                    ocean) */}
                <div
                    className="absolute flex items-center justify-center rounded-sm bg-[#fbf3df]/95 shadow-[inset_0_0_8px_rgba(101,67,33,0.18)]"
                    style={{
                        top: CUTOUT.top,
                        left: CUTOUT.left,
                        right: CUTOUT.right,
                        bottom: CUTOUT.bottom,
                    }}
                >
                    {wordmark ? (
                        <p className="font-serif text-sm italic leading-none text-[#3a2210] sm:text-base">
                            {name}
                        </p>
                    ) : (
                        <img
                            src={`${CARRIER_ASSET_BASE}/${slug}.png`}
                            alt={`${name} logo`}
                            loading="lazy"
                            className="h-full w-full object-contain p-1"
                        />
                    )}
                </div>
                {/* Layer 2 — ornate chest frame */}
                <img
                    src={CHEST_FRAME}
                    alt=""
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 h-full w-full select-none drop-shadow-[0_10px_18px_rgba(0,0,0,0.55)]"
                    loading="eager"
                />
            </div>
            {/* Carrier name plate sits BELOW the chest so long names don't
                clip the chest artwork */}
            <p className="mt-2 line-clamp-2 max-w-[120px] text-center font-mono text-[9px] uppercase leading-tight tracking-[0.06em] text-[#f1d595] sm:max-w-[140px] sm:text-[10px]">
                {name}
            </p>
        </div>
    );
}

export default function CarrierStrip() {
    const loop = [...CARRIERS, ...CARRIERS];

    return (
        <section
            data-testid="carrier-strip"
            aria-labelledby="carrier-strip-heading"
            className="relative isolate overflow-hidden border-t border-[rgba(212,175,55,0.18)] py-16 md:py-20"
            style={{
                background:
                    "linear-gradient(180deg,#04101a 0%,#071a28 40%,#031018 100%)",
            }}
        >
            {/* === Animated ocean backdrop === two layered wave SVGs sliding
                in opposite directions for a parallax water effect. Pure
                CSS, no extra raster assets. */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-[70%] overflow-hidden"
            >
                <svg
                    className="ocean-wave ocean-wave--back absolute inset-x-0 bottom-0 w-[200%]"
                    viewBox="0 0 1440 200"
                    preserveAspectRatio="none"
                    style={{ height: "60%" }}
                >
                    <path
                        d="M0,120 C240,180 480,40 720,100 C960,160 1200,40 1440,90 L1440,200 L0,200 Z"
                        fill="rgba(15,52,82,0.55)"
                    />
                    <path
                        d="M0,120 C240,180 480,40 720,100 C960,160 1200,40 1440,90 L1440,200 L0,200 Z"
                        fill="rgba(15,52,82,0.55)"
                        transform="translate(1440 0)"
                    />
                </svg>
                <svg
                    className="ocean-wave ocean-wave--front absolute inset-x-0 bottom-0 w-[200%]"
                    viewBox="0 0 1440 200"
                    preserveAspectRatio="none"
                    style={{ height: "45%" }}
                >
                    <path
                        d="M0,140 C200,80 480,180 720,130 C1020,70 1240,160 1440,120 L1440,200 L0,200 Z"
                        fill="rgba(8,30,50,0.85)"
                    />
                    <path
                        d="M0,140 C200,80 480,180 720,130 C1020,70 1240,160 1440,120 L1440,200 L0,200 Z"
                        fill="rgba(8,30,50,0.85)"
                        transform="translate(1440 0)"
                    />
                </svg>
                {/* Subtle gold horizon glow */}
                <div
                    className="absolute inset-x-0 top-0 h-px"
                    style={{
                        background:
                            "linear-gradient(90deg,transparent 0%,rgba(212,175,55,0.45) 50%,transparent 100%)",
                    }}
                />
            </div>

            <div className="relative z-10 mx-auto max-w-[1200px] px-6 md:px-10">
                <h2
                    id="carrier-strip-heading"
                    className="font-serif text-3xl font-bold leading-tight text-white md:text-4xl"
                >
                    Trusted Carriers
                </h2>
                <p className="mt-2 text-sm text-brand-muted">
                    Partial listing of insurance carriers.
                </p>
                <p
                    data-testid="carrier-strip-disclosure"
                    className="mt-4 max-w-3xl text-xs italic leading-relaxed text-brand-ivory/70 md:text-sm"
                >
                    Carrier availability varies by state, product line,
                    underwriting eligibility, and agency relationships. Some
                    products may be offered through direct carrier
                    appointments, managing general agencies (MGAs),
                    wholesalers, brokers, or other authorized market access
                    arrangements.
                </p>
            </div>

            <div
                data-testid="carrier-marquee"
                className="carrier-marquee group relative z-10 mt-10 overflow-hidden"
                aria-label="Scrolling list of underwriter logos"
            >
                <div
                    className="carrier-marquee__track flex w-max items-end gap-4 will-change-transform group-hover:[animation-play-state:paused] motion-reduce:[animation:none] sm:gap-6"
                    style={{ animation: "carrier-marquee 80s linear infinite" }}
                >
                    {loop.map((c, i) => (
                        <CarrierChest
                            key={`${c.slug}-${i}`}
                            name={c.name}
                            slug={c.slug}
                            wordmark={c.wordmark}
                        />
                    ))}
                </div>
            </div>

            {/* Soft edge fades on left/right of the marquee */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 left-0 z-20 w-24 bg-gradient-to-r from-[#04101a] to-transparent"
            />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 right-0 z-20 w-24 bg-gradient-to-l from-[#04101a] to-transparent"
            />

            <style>{`
                @keyframes carrier-marquee {
                    0%   { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                }
                @keyframes ocean-back {
                    0%   { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                }
                @keyframes ocean-front {
                    0%   { transform: translateX(-50%); }
                    100% { transform: translateX(0); }
                }
                .ocean-wave--back { animation: ocean-back 22s linear infinite; }
                .ocean-wave--front { animation: ocean-front 14s linear infinite; }
                @media (prefers-reduced-motion: reduce) {
                    .carrier-marquee__track,
                    .ocean-wave--back,
                    .ocean-wave--front {
                        animation: none !important;
                        transform: none !important;
                    }
                }
            `}</style>
        </section>
    );
}
